import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * AreaChip — the small pill that names a discovery area (T-0184).
 *
 * Mirrors `.dchip` in `docs/discovery/prototype/discovery.css`: 20px high,
 * paper-100 fill, an ink-100 hairline and an 11px label. `all` is the
 * dashed "All areas" variant for a question that informs every area.
 * The coverage unit is called "area" on every screen (Decision 48).
 *
 * T-0186 adds the two other prototype variants: `.dchip.edit` (an `×` that
 * removes the area, `onRemove`) and `.dchip.link` (the chip opens the area,
 * `onClick`).
 */

export interface AreaChipProps {
  /** The area's short label. Ignored when `all` is set. */
  children?: React.ReactNode;
  /** Tooltip, usually the area's full name. */
  title?: string;
  all?: boolean;
  /** Show an `×` that removes the area (`.dchip.edit`). */
  onRemove?: () => void;
  /** The chip is a button that opens the area (`.dchip.link`). */
  onClick?: () => void;
  className?: string;
}

const BASE =
  'inline-flex h-5 items-center gap-1 whitespace-nowrap rounded-full border px-2 text-[11px] font-medium leading-4';

export function AreaChip({
  children,
  title,
  all = false,
  onRemove,
  onClick,
  className,
}: AreaChipProps) {
  const label = all ? 'All areas' : children;
  if (onClick) {
    return (
      <button
        type="button"
        title={title}
        onClick={onClick}
        className={cn(
          BASE,
          'border-ink-100 bg-paper-0 text-ink-700 hover:border-ink-300 hover:text-ink-900',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
          className,
        )}
      >
        {label}
      </button>
    );
  }
  return (
    <span
      title={title}
      className={cn(
        BASE,
        all
          ? 'border-dashed border-ink-200 bg-paper-0 text-ink-500'
          : 'border-ink-100 bg-paper-100 text-ink-700',
        onRemove && 'pr-[3px]',
        className,
      )}
    >
      {label}
      {onRemove ? (
        <button
          type="button"
          aria-label={`Remove ${typeof children === 'string' ? children : 'area'}`}
          onClick={onRemove}
          className="inline-flex h-4 w-4 items-center justify-center rounded-full text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          <X className="h-2.5 w-2.5" aria-hidden="true" />
        </button>
      ) : null}
    </span>
  );
}

/**
 * "+ Area" (`.dchip-add`): a small dashed select that adds one of the
 * areas not already on the item.
 */
export function AreaAddSelect({
  options,
  onAdd,
  label = '+ Area',
}: {
  options: ReadonlyArray<{ id: string; label: string }>;
  onAdd: (id: string) => void;
  label?: string;
}) {
  if (options.length === 0) return null;
  return (
    <select
      aria-label="Add an area"
      value=""
      onChange={(e) => {
        if (e.target.value) onAdd(e.target.value);
      }}
      className="h-[22px] w-24 cursor-pointer rounded-full border border-dashed border-ink-300 bg-paper-0 px-1.5 text-[11px] font-medium leading-4 text-ink-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
    >
      <option value="">{label}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
